  import React, { useState, useCallback } from 'react';
  import { useNavigate, Link } from 'react-router-dom';
  import { useTranslation, Trans } from 'react-i18next';
  import { useAuth } from '@/contexts/AuthContext';
  import { useCurrency } from '@/contexts/CurrencyContext';
  import Card from '@/components/atoms/Card';
  import Button from '@/components/atoms/Button';
  import { FormField } from '@/components/molecules/FormField';
  import PasswordStrengthIndicator from '@/components/atoms/PasswordStrengthIndicator';
  import SSOLoginOptions from '@/components/organisms/SSOLoginOptions/SSOLoginOptions';
  import { validateRegistrationForm, validateEmail, validateRequired, validatePassword, validatePasswordMatch, validateDateOfBirth } from '@/utils/validation';
  import './Register.scss';

  interface RegisterFormData {
    email: string;
    dateOfBirth: string;
    password: string;
    confirmPassword: string;
    acceptTerms: boolean;
    role: 'customer';
  }

  const initialFormData: RegisterFormData = {
    email: '',
    dateOfBirth: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
    role: 'customer'
  };

  const initialErrors: Record<string, string> = {};

  const Register: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { register } = useAuth();
    const { currency } = useCurrency();
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [formData, setFormData] = useState<RegisterFormData>(initialFormData);
    const [errors, setErrors] = useState<Record<string, string>>(initialErrors);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Set document language to match currency locale for proper date formatting
    React.useEffect(() => {
      if (currency.locale) {
        document.documentElement.lang = currency.locale;
      }
    }, [currency.locale]);

    const handleInputChange = useCallback((field: keyof RegisterFormData, value: string | boolean) => {
      setFormData(prev => ({ ...prev, [field]: value }));
      
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }, [errors]);

    const handleFieldBlur = useCallback((field: keyof RegisterFormData) => {
      // Validate the specific field when user leaves it
      const fieldValue = formData[field];
      let fieldError: string | null = null;

      switch (field) {
        case 'email':
          fieldError = validateEmail(fieldValue as string, t);
          break;
        case 'dateOfBirth':
          fieldError = validateDateOfBirth(fieldValue as string, 18, t);
          break;
        case 'password':
          fieldError = validatePassword(fieldValue as string, t);
          break;
        case 'confirmPassword':
          fieldError = validatePasswordMatch(formData.password, fieldValue as string, t);
          break;
        case 'acceptTerms':
          if (!fieldValue) {
            fieldError = t('errors.validation.acceptTerms');
          }
          break;
      }

      if (fieldError) {
        setErrors(prev => ({ ...prev, [field]: fieldError }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }, [formData, t]);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
      e.preventDefault();
      setIsSubmitting(true);
      setErrors({});

      // Validate form using utility function
      const validationErrors = validateRegistrationForm({
        email: formData.email,
        dateOfBirth: formData.dateOfBirth,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        acceptTerms: formData.acceptTerms
      }, t);

      // If there are validation errors, return them
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        setIsSubmitting(false);
        return;
      }

      // Try to register
      try {
        await register({
          email: formData.email.toLowerCase().trim(),
          date_of_birth: formData.dateOfBirth,
          password: formData.password,
          role: formData.role
        });
        
        // Registration successful, redirect to dashboard
        navigate('/dashboard');
      } catch (error: any) {
        let errorMessage = error.response?.data?.detail || 
                        error.response?.data?.message || 
                        error.message || 
                        'Registration failed';
        setErrors({
          general: errorMessage
        });
        setIsSubmitting(false);
      }
    }, [formData, register, navigate, t]);

    return (
      <div className="register-page">
        <div className="container">
          <div className="register-page__content">
            <Card variant="elevated" padding="xl" className="register-card">
              <div className="register-card__header">
                <h1 className="register-card__title">{t('auth.register.title')}</h1>
                <p className="register-card__subtitle">{t('auth.register.subtitle')}</p>
              </div>

              {/* SSO Registration Options - Arriba */}
              <SSOLoginOptions
                onSuccess={() => {
                  // El usuario se registró con SSO, redirigir según rol
                  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
                  
                  if (currentUser.role === 'barman') {
                    navigate('/barman');
                  } else if (currentUser.role === 'customer') {
                    navigate('/events');
                  } else if (currentUser.role === 'organizer') {
                    navigate('/dashboard');
                  } else {
                    navigate('/dashboard');
                  }
                }}
                onError={(error) => {
                  const errorMessage = error?.message || t('auth.register.error');
                  setErrors({ general: errorMessage });
                }}
                showDivider={true}
                dividerText={t('auth.sso.registerDividerText')}
              />

              <form onSubmit={handleSubmit} className="register-card__form">
                {errors.general && (
                  <div className="register-card__error">
                    {errors.general}
                  </div>
                )}

                <div className="register-card__fields">
                  <FormField
                    type="email"
                    name="email"
                    label={t('auth.register.email')}
                    error={errors.email}
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    onBlur={() => handleFieldBlur('email')}
                    placeholder={t('auth.register.emailPlaceholder')}
                    disabled={isSubmitting}
                  />

                  <FormField
                    type="date"
                    name="dateOfBirth"
                    label={t('auth.register.dateOfBirth')}
                    error={errors.dateOfBirth}
                    /*helperText={t('auth.register.dateOfBirthHelp')}*/
                    required
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                    onBlur={() => handleFieldBlur('dateOfBirth')}
                    disabled={isSubmitting}
                    max={(() => {
                      // Calculate the maximum date (18 years ago from today)
                      const today = new Date();
                      const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
                      return maxDate.toISOString().split('T')[0];
                    })()}
                    lang={currency.locale}
                    inputClassName="register-card__date-input"
                  />

                  {/* Role is fixed to customer for public registration */}
                  <input type="hidden" name="role" value="customer" />

                  <FormField
                    type="password"
                    name="password"
                    label={t('auth.register.password')}
                    error={errors.password}
                    required
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    onBlur={() => handleFieldBlur('password')}
                    placeholder={t('auth.register.password')}
                    disabled={isSubmitting}
                  />
                  
                  <PasswordStrengthIndicator 
                    password={formData.password}
                    className="register-card__password-strength"
                  />

                  <FormField
                    type="password"
                    name="confirmPassword"
                    label={t('auth.register.confirmPassword')}
                    error={errors.confirmPassword}
                    required
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    onBlur={() => handleFieldBlur('confirmPassword')}
                    placeholder={t('auth.register.confirmPassword')}
                    disabled={isSubmitting}
                  />

                  <div className="register-card__terms">
                    <label className="register-card__terms-label">
                      <input
                        type="checkbox"
                        name="acceptTerms"
                        checked={formData.acceptTerms}
                        onChange={(e) => handleInputChange('acceptTerms', e.target.checked)}
                        onBlur={() => handleFieldBlur('acceptTerms')}
                        disabled={isSubmitting}
                        className="register-card__terms-checkbox"
                      />
                      <span className="register-card__terms-text">
                        {t('auth.register.acceptTerms')}{' '}
                        <button
                          type="button"
                          onClick={() => setShowTermsModal(true)}
                          className="register-card__terms-link"
                        >
                          {t('auth.register.viewTerms')}
                        </button>
                      </span>
                    </label>
                    {errors.acceptTerms && (
                      <div className="register-card__terms-error">
                        {errors.acceptTerms}
                      </div>
                    )}
                  </div>
                </div>

                <div className="register-card__actions">
                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    fullWidth
                    disabled={isSubmitting}
                    className="register-card__submit-btn"
                  >
                    {isSubmitting ? t('common.loading') : t('auth.register.registerButton')}
                  </Button>
                </div>

                <div className="register-card__footer">
                  <p className="register-card__login-link">
                    {t('auth.register.hasAccount')}{' '}<br/>
                    <Link to="/login" className="register-card__link">
                      {t('auth.register.loginLink')}
                    </Link>
                  </p>
                  <p className="register-card__login-link">
                    <Link to="/forgot-password" className="register-card__link">
                      {t('auth.login.forgotPassword')}
                    </Link>
                  </p>
                  <p className="register-card__home-link">
                    <Link to="/events" className="register-card__link">
                      {t('navigation.events')}
                    </Link>
                  </p>
                </div>
              </form>
            </Card>
          </div>
        </div>

        {/* Terms and Conditions Modal */}
        {showTermsModal && (
          <div className="terms-modal-overlay" onClick={() => setShowTermsModal(false)}>
            <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
              <div className="terms-modal__header">
                <h2>{t('auth.register.termsTitle')}</h2>
                <button
                  type="button"
                  onClick={() => setShowTermsModal(false)}
                  className="terms-modal__close"
                  aria-label={t('common.close')}
                >
                  ×
                </button>
              </div>
              <div className="terms-modal__content">
                <div className="terms-modal__text">
                  <h3>{t('auth.register.termsSection1.title')}</h3>
                    <Trans i18nKey="auth.register.termsSection1.content"
                    components={{
                      p: <p />,
                      ul: <ul />,
                      li: <li />,
                      strong: <strong />
                    }}
                  />
                  <h3>{t('auth.register.termsSection2.title')}</h3>
                    <Trans
                    i18nKey="auth.register.termsSection2.content"
                    components={{
                      p: <p />,
                      ul: <ul />,
                      li: <li />,
                      strong: <strong />
                    }}
                  />
                  
                  <h3>{t('auth.register.termsSection3.title')}</h3>
                    <Trans
                    i18nKey="auth.register.termsSection3.content"
                    components={{
                      p: <p />,
                      ul: <ul />,
                      li: <li />,
                      strong: <strong />
                    }}
                  />
                  
                  <h3>{t('auth.register.termsSection4.title')}</h3>
                    <Trans
                    i18nKey="auth.register.termsSection4.content"
                    components={{
                      p: <p />,
                      ul: <ul />,
                      li: <li />,
                      strong: <strong />
                    }}
                  />

                  <h3>{t('auth.register.termsSection5.title')}</h3>
                  <Trans
                  i18nKey="auth.register.termsSection5.content"
                components = {{
                  h3: <h3 />,
                    p: <p />,
                    ul: <ul />,
                    li: <li />,
                    strong: <strong />
                  }}
                />

                <h3>{t('auth.register.termsSection6.title')}</h3>
                <Trans
                i18nKey="auth.register.termsSection6.content"
              components = {{
                h3: <h3 />,
                  p: <p />,
                  ul: <ul />,
                  li: <li />,
                  strong: <strong />
                }}
              />

                <h3>{t('auth.register.termsSection7.title')}</h3>
                <Trans
                i18nKey="auth.register.termsSection7.content"
                components={{
                  p: <p />,
                  ul: <ul />,
                  li: <li />,
                  strong: <strong />
                }}
              />

                <h3>{t('auth.register.termsSection8.title')}</h3>
                <Trans
                i18nKey="auth.register.termsSection8.content"
              components = {{
                h3: <h3 />,
                  p: <p />,
                  ul: <ul />,
                  li: <li />,
                  strong: <strong />
                }}
              />

                <h3>{t('auth.register.termsSection9.title')}</h3>
                <Trans
                i18nKey="auth.register.termsSection9.content"
              components = {{
                h3: <h3 />,
                  p: <p />,
                  ul: <ul />,
                  li: <li />,
                  strong: <strong />
                }}
              />

                <h3>{t('auth.register.termsSection10.title')}</h3>
                <Trans
                i18nKey="auth.register.termsSection10.content"
              components = {{
                h3: <h3 />,
                  p: <p />,
                  ul: <ul />,
                  li: <li />,
                  strong: <strong />
                }}
              />

                <h3>{t('auth.register.termsSection11.title')}</h3>

                <Trans
                i18nKey="auth.register.termsSection11.content"
              components = {{
                h3: <h3 />,
                  p: <p />,
                  ul: <ul />,
                  li: <li />,
                  strong: <strong />
                }}
              />

                
                <h3>{t('auth.register.termsSection12.title')}</h3>
                <Trans
                i18nKey="auth.register.termsSection12.content"
              components = {{
                h3: <h3 />,
                  p: <p />,
                  ul: <ul />,
                  li: <li />,
                  strong: <strong />
                }}
              />
                
                <h3>{t('auth.register.termsSection13.title')}</h3>
                <Trans
                i18nKey="auth.register.termsSection13.content"
              components = {{
                h6: <h6 />,
                  p: <p />,
                  ul: <ul />,
                  li: <li />,
                  strong: <strong />
                }}
              />
                
                <h3>{t('auth.register.termsSection14.title')}</h3>
                <Trans
                i18nKey="auth.register.termsSection14.content"
              components = {{
                h6: <h6 />,
                  p: <p />,
                  ul: <ul />,
                  li: <li />,
                  strong: <strong />
                }}
              />

                <h3>{t('auth.register.termsSection15.title')}</h3>
                <Trans
                i18nKey="auth.register.termsSection15.content"
              components = {{
                h5: <h5 />,
                h6: <h6 />,
                  p: <p />,
                  ul: <ul />,
                  li: <li />,
                  strong: <strong />
                }}
              />

                <h3>{t('auth.register.termsSection16.title')}</h3>
                <Trans
                i18nKey="auth.register.termsSection16.content"
              components = {{
                h3: <h3 />,
                  p: <p />,
                  ul: <ul />,
                  li: <li />,
                  strong: <strong />
                }}
              />

                <h3>{t('auth.register.termsSection17.title')}</h3>
                <Trans
                i18nKey="auth.register.termsSection17.content"
              components = {{
                h3: <h3 />,
                  p: <p />,
                  ul: <ul />,
                  li: <li />,
                  strong: <strong />
                }}
              />
                </div>
              </div>
              <div className="terms-modal__footer">
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => setShowTermsModal(false)}
                >
                  {t('common.accept')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  export default Register; 